import { useState, useEffect, useRef } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Loader2, CreditCard, Lock, CheckCircle, AlertCircle, FlaskConical } from 'lucide-react';
import { usePropertyContext } from '@/context/PropertyContext';
import { api, type ExperienceDetail } from '@/lib/api';
import { getStripe, getStripeAppearance } from '@/lib/stripe';
import { cn, formatPrice } from '@/lib/utils';
import { trackPaymentStarted, trackPaymentFailed, updateAbandonmentStep } from '@/lib/analytics';
import { PricingBreakdown } from './PricingBreakdown';
import type { BookingData, BookingPricing } from './BookingFlow';

interface PaymentStepProps {
  bookingId: string;
  bookingNumber: string;
  experience: ExperienceDetail;
  bookingData: BookingData;
  pricing: BookingPricing;
  isDemo: boolean;
  onSuccess: () => void;
  onBack: () => void;
}

export function PaymentStep(props: PaymentStepProps) {
  const { propertyKey, config } = usePropertyContext();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const hasTrackedPaymentStart = useRef(false);

  useEffect(() => {
    async function createPaymentIntent() {
      if (!propertyKey || props.isDemo) {
        setIsLoading(false);
        return;
      }

      // Track payment started (only once)
      if (!hasTrackedPaymentStart.current) {
        hasTrackedPaymentStart.current = true;
        trackPaymentStarted(
          props.experience.id,
          props.pricing.subtotal,
          props.experience.currency || 'USD'
        );
        updateAbandonmentStep('payment', props.experience.id);
      }

      try {
        const result = await api.createPaymentIntent(propertyKey, {
          bookingId: props.bookingId,
        });
        setClientSecret(result.clientSecret);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize payment';
        setError(errorMessage);
        trackPaymentFailed(props.experience.id, errorMessage);
      } finally {
        setIsLoading(false);
      }
    }

    createPaymentIntent();
  }, [propertyKey, props.bookingId, props.isDemo, props.experience.id, props.experience.currency, props.pricing.subtotal]);

  // Demo mode - skip real payment
  if (props.isDemo) {
    return (
      <DemoPayment
        experience={props.experience}
        pricing={props.pricing}
        onSuccess={props.onSuccess}
        onBack={props.onBack}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-[300px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="mt-4 text-muted-foreground">Preparing payment...</p>
      </div>
    );
  }

  if (error || !clientSecret) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
          <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-4" />
          <h3 className="font-semibold text-destructive">Payment Error</h3>
          <p className="mt-2 text-sm text-muted-foreground">{error || 'Failed to initialize payment'}</p>
          <button
            onClick={props.onBack}
            className="mt-4 rounded-lg px-4 py-2 text-sm font-medium border hover:bg-muted"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const primaryColor = config?.widgetSettings?.primaryColor || '#0f172a';
  const stripePromise = getStripe();
  const appearance = getStripeAppearance(primaryColor);

  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
      <PaymentForm
        experience={props.experience}
        pricing={props.pricing}
        onSuccess={props.onSuccess}
        onBack={props.onBack}
      />
    </Elements>
  );
}

interface PaymentFormProps {
  experience: ExperienceDetail;
  pricing: BookingPricing;
  onSuccess: () => void;
  onBack: () => void;
}

function PaymentForm({ experience, pricing, onSuccess, onBack }: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  // Categorize errors for better UX messaging
  const getErrorCategory = (errorMessage: string): 'card' | 'network' | 'declined' | 'unknown' => {
    const lowerMsg = errorMessage.toLowerCase();
    if (lowerMsg.includes('card') || lowerMsg.includes('cvc') || lowerMsg.includes('expir')) {
      return 'card';
    }
    if (lowerMsg.includes('network') || lowerMsg.includes('connection') || lowerMsg.includes('timeout')) {
      return 'network';
    }
    if (lowerMsg.includes('decline') || lowerMsg.includes('insufficient') || lowerMsg.includes('fraud')) {
      return 'declined';
    }
    return 'unknown';
  };

  const getErrorGuidance = (category: 'card' | 'network' | 'declined' | 'unknown'): string => {
    switch (category) {
      case 'card':
        return 'Please check your card number, expiration date, and CVC.';
      case 'network':
        return 'Please check your internet connection and try again.';
      case 'declined':
        return 'Your card was declined. Please try a different payment method or contact your bank.';
      default:
        return 'Please try again or use a different payment method.';
    }
  };

  const handleRetry = () => {
    setError(null);
    setRetryCount(prev => prev + 1);
    // Reset the payment element to allow re-entry
    elements?.getElement('payment')?.clear();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setError(null);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        const errorMessage = submitError.message || 'Payment failed';
        setError(errorMessage);
        trackPaymentFailed(experience.id, errorMessage);
        setIsProcessing(false);
        return;
      }

      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/success`,
        },
        redirect: 'if_required',
      });

      if (confirmError) {
        setError(confirmError.message || 'Payment failed');
        trackPaymentFailed(experience.id, confirmError.message || 'Payment failed');
        setIsProcessing(false);
      } else {
        onSuccess();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Payment failed';
      setError(errorMessage);
      trackPaymentFailed(experience.id, errorMessage);
      setIsProcessing(false);
    }
  };

  const errorCategory = error ? getErrorCategory(error) : null;;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold">Payment</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Complete your booking for {experience.name}
        </p>
      </div>

      {/* Pricing Breakdown */}
      <div className="mb-6 rounded-lg bg-muted/50 p-4">
        <PricingBreakdown
          baseAmount={pricing.baseAmount + pricing.childAmount}
          addOnsAmount={pricing.addOnsAmount}
          discountAmount={pricing.discountAmount}
          serviceFee={pricing.serviceFee}
          serviceFeeRate={pricing.serviceFeeRate}
          taxAmount={pricing.taxAmount}
          totalAmount={pricing.totalAmount}
          currency={experience.currency}
          feeDisplayMode={pricing.feeDisplayMode}
          baseLabel={experience.name}
        />
      </div>

      <form onSubmit={handleSubmit}>
        {/* Stripe Payment Element */}
        <div className="rounded-lg border p-4">
          <PaymentElement options={{ layout: 'tabs' }} />
        </div>

        {/* Error Display with Retry */}
        {error && errorCategory && (
          <div className="mt-4 rounded-lg border border-destructive/50 bg-destructive/10 p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-destructive">Payment Failed</p>
                <p className="mt-1 text-sm text-destructive/80">{error}</p>
                <p className="mt-2 text-xs text-muted-foreground">
                  {getErrorGuidance(errorCategory)}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleRetry}
              className="mt-3 w-full rounded-lg border border-destructive/30 bg-background px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/5 transition-colors"
            >
              {retryCount >= 2 ? 'Try Different Payment Method' : 'Try Again'}
            </button>
            {retryCount >= 2 && (
              <p className="mt-2 text-xs text-center text-muted-foreground">
                Having trouble? Contact support or try a different card.
              </p>
            )}
          </div>
        )}

        {/* Security Note */}
        <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
          <Lock className="h-3 w-3" />
          <span>Payments are secure and encrypted</span>
        </div>

        {/* Navigation */}
        <div className="mt-6 flex justify-between">
          <button
            type="button"
            onClick={onBack}
            disabled={isProcessing}
            className="rounded-lg px-6 py-3 font-medium text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={!stripe || isProcessing}
            className={cn(
              'rounded-lg px-6 py-3 font-medium transition-colors',
              'bg-primary text-primary-foreground hover:bg-primary/90',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin inline" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard className="mr-2 h-4 w-4 inline" />
                Pay {formatPrice(pricing.totalAmount, experience.currency)}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

// Demo Payment - simulates payment without real charge
interface DemoPaymentProps {
  experience: ExperienceDetail;
  pricing: BookingPricing;
  onSuccess: () => void;
  onBack: () => void;
}

function DemoPayment({ experience, pricing, onSuccess, onBack }: DemoPaymentProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const handleDemoPayment = async () => {
    setIsProcessing(true);
    // Simulate payment processing delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsComplete(true);
    // Short delay to show success state
    await new Promise(resolve => setTimeout(resolve, 1000));
    onSuccess();
  };

  if (isComplete) {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-[300px]">
        <div className="rounded-full bg-success/10 p-4 mb-4">
          <CheckCircle className="h-10 w-10 text-success" />
        </div>
        <h3 className="text-lg font-semibold">Demo Payment Complete!</h3>
        <p className="mt-2 text-sm text-muted-foreground">Redirecting to confirmation...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold">Demo Payment</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Complete your demo booking for {experience.name}
        </p>
      </div>

      {/* Demo Notice */}
      <div className="mb-6 rounded-lg border border-warning/30 bg-warning/5 p-4">
        <div className="flex items-start gap-3">
          <FlaskConical className="h-5 w-5 text-warning shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-warning-foreground">Demo Mode Active</p>
            <p className="mt-1 text-sm text-warning-foreground/80">
              No real payment will be processed. Click below to simulate a successful booking.
            </p>
          </div>
        </div>
      </div>

      {/* Pricing Breakdown */}
      <div className="mb-6 rounded-lg bg-muted/50 p-4">
        <PricingBreakdown
          baseAmount={pricing.baseAmount + pricing.childAmount}
          addOnsAmount={pricing.addOnsAmount}
          discountAmount={pricing.discountAmount}
          serviceFee={pricing.serviceFee}
          serviceFeeRate={pricing.serviceFeeRate}
          taxAmount={pricing.taxAmount}
          totalAmount={pricing.totalAmount}
          currency={experience.currency}
          feeDisplayMode={pricing.feeDisplayMode}
          baseLabel={experience.name}
        />
        <p className="mt-3 text-xs text-muted-foreground">
          * This amount will not be charged in demo mode
        </p>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          disabled={isProcessing}
          className="rounded-lg px-6 py-3 font-medium text-muted-foreground hover:text-foreground disabled:opacity-50"
        >
          Back
        </button>
        <button
          onClick={handleDemoPayment}
          disabled={isProcessing}
          className={cn(
            'rounded-lg px-6 py-3 font-medium transition-colors',
            'bg-warning text-warning-foreground hover:bg-warning/90',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          {isProcessing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin inline" />
              Processing Demo...
            </>
          ) : (
            <>
              <FlaskConical className="mr-2 h-4 w-4 inline" />
              Complete Demo Booking
            </>
          )}
        </button>
      </div>
    </div>
  );
}
